let cachedClipboardText = "";
let cachedClipboardValue = "";

const CLIPBOARD_IMAGE_EXTENSIONS = {
	"image/png": "png",
	"image/jpeg": "jpg",
	"image/jpg": "jpg",
	"image/gif": "gif",
	"image/webp": "webp",
	"image/bmp": "bmp",
	"image/svg+xml": "svg"
};

function normalizeClipboardText(value) {
	if (typeof value === "string") {
		return value;
	}
	if (value === null || typeof value === "undefined") {
		return "";
	}
	try {
		return JSON.stringify(value);
	} catch (err) {
		return String(value);
	}
}

function getClipboardImageExtension(contentType = "") {
	return CLIPBOARD_IMAGE_EXTENSIONS[`${contentType || ""}`.toLowerCase()] || "png";
}

function getClipboardImageFileName(contentType = "", index = 0) {
	const suffix = index > 0 ? `-${index + 1}` : "";
	const timestamp = (new Date()).toISOString().split(".")[0].replace(/:/g, "");
	return `clipboard-image-${timestamp}${suffix}.${getClipboardImageExtension(contentType)}`;
}

function createFileFromBlob(blob, fileName) {
	const contentType = blob && blob.type ? blob.type : "";
	try {
		return new File([blob], fileName, { type: contentType });
	} catch (_err) {
		blob.name = fileName;
		return blob;
	}
}

function readBlobAsText(blob) {
	if (!blob) {
		return Promise.resolve("");
	}
	if (typeof blob.text === "function") {
		return blob.text();
	}
	return new Promise((resolve, reject) => {
		try {
			const reader = new FileReader();
			reader.onload = () => resolve(`${reader.result || ""}`);
			reader.onerror = () => reject(reader.error || new Error("Unable to read clipboard text."));
			reader.readAsText(blob);
		} catch (err) {
			reject(err);
		}
	});
}

function createFileFromDataUri(dataUri = "", index = 0) {
	const match = `${dataUri || ""}`.match(/^data:(image\/[^;,]+);base64,(.+)$/i);
	if (!match) {
		return null;
	}

	const contentType = match[1];
	const bytes = atob(match[2]);
	const array = new Uint8Array(bytes.length);
	for (let i = 0; i < bytes.length; i++) {
		array[i] = bytes.charCodeAt(i);
	}

	const blob = new Blob([array], { type: contentType });
	return createFileFromBlob(blob, getClipboardImageFileName(contentType, index));
}

function getDataImageFromHtml(html = "") {
	const match = `${html || ""}`.match(/<img[^>]+src=["'](data:image\/[^"']+)["']/i);
	return match ? match[1] : "";
}

const initializeClipboardCache = () => {
	if (navigator?.clipboard?.readText) {
		navigator.clipboard.readText().then((text) => {
			if (typeof text === "string") {
				cachedClipboardText = text;
				cachedClipboardValue = text;
			}
		}).catch(() => {
			// ignore - clipboard permissions not granted
		});
	}
};

initializeClipboardCache();

let toastHandler = null;

export function registerClipboardToastCallback(callback) {
	toastHandler = callback;
}

function triggerToast() {
	if (typeof toastHandler === "function") {
		try {
			toastHandler();
		} catch (err) {
			console.warn("Clipboard toast callback failed", err);
		}
	}
}

export async function copyToClipboard(text) {
	const normalizedText = normalizeClipboardText(text);
	cachedClipboardText = normalizedText;
	cachedClipboardValue = text === null || typeof text === "undefined" ? "" : text;

	if (navigator?.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(normalizedText);
			triggerToast();
			return true;
		} catch (err) {
			console.warn("navigator.clipboard.writeText failed", err);
		}
	}

	try {
		const textarea = document.createElement("textarea");
		textarea.value = normalizedText;
		textarea.setAttribute("readonly", "");
		textarea.style.position = "fixed";
		textarea.style.opacity = "0";
		document.body.appendChild(textarea);
		textarea.focus();
		textarea.select();
		const succeeded = document.execCommand("copy");
		document.body.removeChild(textarea);
		if (succeeded) {
			triggerToast();
		}
		return succeeded;
	} catch (err) {
		console.warn("Fallback clipboard copy failed", err);
		return false;
	}
}

export const copy = copyToClipboard;

export async function readClipboardText() {
	if (navigator?.clipboard?.readText) {
		try {
			const text = await navigator.clipboard.readText();
			if (typeof text === "string") {
				cachedClipboardText = text;
				cachedClipboardValue = text;
				return text;
			}
		} catch (err) {
			console.warn("navigator.clipboard.readText failed", err);
		}
	}
	return cachedClipboardText;
}

export async function readClipboardContents() {
	const result = {
		text: "",
		files: []
	};

	if (navigator?.clipboard?.read) {
		try {
			const clipboardItems = await navigator.clipboard.read();
			for (const clipboardItem of clipboardItems) {
				const types = Array.isArray(clipboardItem.types) ? clipboardItem.types : [];
				for (const type of types) {
					if (`${type || ""}`.toLowerCase().indexOf("image/") === 0) {
						const blob = await clipboardItem.getType(type);
						result.files.push(createFileFromBlob(blob, getClipboardImageFileName(blob.type || type, result.files.length)));
					}
				}
				for (const type of types) {
					if (!result.text && (type === "text/plain" || type === "application/json" || type === "text/html")) {
						const blob = await clipboardItem.getType(type);
						const text = await readBlobAsText(blob);
						if (type === "text/html") {
							const dataImage = getDataImageFromHtml(text);
							const file = dataImage ? createFileFromDataUri(dataImage, result.files.length) : null;
							if (file) {
								result.files.push(file);
							}
						} else if (typeof text === "string") {
							result.text = text;
						}
					}
				}
			}
		} catch (err) {
			console.warn("navigator.clipboard.read failed", err);
		}
	}

	if (!result.text && result.files.length === 0) {
		result.text = await readClipboardText();
	}

	return result;
}

export function readPasteEventContents(event) {
	const clipboardData = event && event.clipboardData ? event.clipboardData : null;
	const result = {
		text: "",
		files: []
	};

	if (!clipboardData) {
		return result;
	}

	const items = Array.from(clipboardData.items || []);
	items.forEach((item) => {
		const contentType = `${item && item.type ? item.type : ""}`.toLowerCase();
		if (item && item.kind === "file" && contentType.indexOf("image/") === 0 && typeof item.getAsFile === "function") {
			const file = item.getAsFile();
			if (file) {
				result.files.push(file);
			}
		}
	});

	if (result.files.length === 0) {
		Array.from(clipboardData.files || []).forEach((file) => {
			const contentType = `${file && file.type ? file.type : ""}`.toLowerCase();
			if (file && contentType.indexOf("image/") === 0) {
				result.files.push(file);
			}
		});
	}

	const html = clipboardData.getData ? clipboardData.getData("text/html") : "";
	if (result.files.length === 0 && html) {
		const dataImage = getDataImageFromHtml(html);
		const file = dataImage ? createFileFromDataUri(dataImage, result.files.length) : null;
		if (file) {
			result.files.push(file);
		}
	}

	result.text = clipboardData.getData ? (clipboardData.getData("text/plain") || clipboardData.getData("application/json") || "") : "";
	return result;
}

export function getClipboard() {
	return cachedClipboardValue;
}

export function isClipboardEmpty() {
	return cachedClipboardValue === null || typeof cachedClipboardValue === "undefined" || cachedClipboardValue === "";
}
